import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart } from "lucide-react";
import { BsStarFill, BsStars } from "react-icons/bs";
import { PulseLoader } from "react-spinners";

/* Seller descriptions are stored as HTML -> one line of plain text.
   DOMParser never runs scripts and decodes entities (&amp; etc.). */
export const htmlToText = (html) => {
    if (!html) return "";
    const spaced = String(html)
        .replace(/<\s*br\s*\/?>/gi, " ")
        .replace(/<\/(p|div|li|h[1-6]|tr|ul|ol)>/gi, " ");
    const doc = new DOMParser().parseFromString(spaced, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

const ProductCard = ({
    product,
    imageSrc,
    onImageError,
    showWishlist,
    isWishlisted,
    wishlistLoading,
    cartItem,
    cartLoading,
    onToggleWishlist,
    onAddToCart,
    onQuantityChange,
}) => {
    const variant = product.variants?.[0];
    const description = htmlToText(product.description);
    const hasDiscount = variant?.mrp > variant?.price;
    const discount = hasDiscount ? Math.round((1 - variant.price / variant.mrp) * 100) : 0;
    const rating = Number(product.averageRating || 0);

    const renderAction = () => {
        if (!product.isInStock) {
            return (
                <button
                    disabled
                    className="w-full py-2 rounded-md bg-gray-100 text-gray-400 text-xs sm:text-sm font-semibold cursor-not-allowed"
                >
                    Out of Stock
                </button>
            );
        }

        if (cartItem) {
            return (
                <div className="flex items-center justify-between rounded-md border border-yellow-800 overflow-hidden">
                    <button
                        type="button"
                        aria-label="Decrease quantity"
                        disabled={cartLoading}
                        onClick={() => onQuantityChange(product, cartItem.quantity - 1)}
                        className="w-10 h-9 text-lg text-yellow-800 hover:bg-yellow-50 disabled:opacity-50"
                    >
                        −
                    </button>
                    <span className="text-sm font-semibold text-gray-800 min-w-[1.5rem] text-center">
                        {cartLoading ? <PulseLoader size={4} color="#854d0e" /> : cartItem.quantity}
                    </span>
                    <button
                        type="button"
                        aria-label="Increase quantity"
                        disabled={cartLoading}
                        onClick={() => onQuantityChange(product, cartItem.quantity + 1)}
                        className="w-10 h-9 text-lg text-yellow-800 hover:bg-yellow-50 disabled:opacity-50"
                    >
                        +
                    </button>
                </div>
            );
        }

        return (
            <button
                type="button"
                disabled={cartLoading}
                onClick={() => onAddToCart(product)}
                className="w-full h-9 flex items-center justify-center gap-1.5 rounded-md border border-yellow-800 text-yellow-800 text-xs sm:text-sm font-semibold hover:bg-yellow-50 transition-colors disabled:opacity-60"
            >
                {cartLoading ? (
                    <PulseLoader size={5} color="#854d0e" />
                ) : (
                    <>
                        <ShoppingCart size={15} />
                        Add to Cart
                    </>
                )}
            </button>
        );
    };

    return (
        <article className="relative flex flex-col h-full bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md hover:border-yellow-800/40 transition-all">
            {/* Wishlist (outside the Link so it doesn't navigate) */}
            {showWishlist && variant && (
                <button
                    type="button"
                    aria-label="Wishlist"
                    onClick={() => onToggleWishlist(product._id, variant._id)}
                    disabled={wishlistLoading}
                    className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-red-500 shadow-sm"
                >
                    {wishlistLoading ? (
                        <PulseLoader size={3} color="#854d0e" />
                    ) : (
                        <Heart
                            size={16}
                            className={isWishlisted ? "text-red-500" : ""}
                            fill={isWishlisted ? "currentColor" : "none"}
                        />
                    )}
                </button>
            )}

            <Link to={`/product/${product.slug}`} className="flex flex-col flex-1">
                {/* Image: fixed h-64 container */}
                <div className="relative h-64 w-full bg-gray-50 flex items-center justify-center overflow-hidden">
                    <img
                        src={imageSrc}
                        alt={product.name}
                        loading="lazy"
                        onError={onImageError}
                        className={`max-h-full max-w-full object-contain p-3 ${!product.isInStock ? "grayscale opacity-60" : ""
                            }`}
                    />
                    {product.promotion && product.isInStock && (
                        <span className="absolute top-0 left-0 flex items-center gap-1 bg-yellow-800 text-white text-[10px] font-semibold px-2 py-1 rounded-br-md">
                            <BsStars /> Deal
                        </span>
                    )}
                    {!product.isInStock && (
                        <span className="absolute bottom-2 left-2 bg-gray-800 text-white text-[10px] font-medium px-2 py-0.5 rounded">
                            Sold out
                        </span>
                    )}
                </div>

                {/* Info */}
                <div className="p-3 flex flex-col gap-1.5 flex-1">
                    <h3 className="text-sm font-medium text-gray-800 line-clamp-1">{product.name}</h3>

                    {/* Plain text, max 2 lines then "..." */}
                    <p className="text-xs text-gray-500 leading-4 line-clamp-2 min-h-[2rem]">
                        {description || "No description available"}
                    </p>

                    <div className="flex items-baseline flex-wrap gap-x-2">
                        <span className="text-base font-bold text-gray-900">
                            ₹{variant?.price?.toLocaleString("en-IN") ?? "0"}
                        </span>
                        {hasDiscount && (
                            <>
                                <span className="text-xs text-gray-400 line-through">
                                    ₹{variant.mrp.toLocaleString("en-IN")}
                                </span>
                                <span className="text-xs font-semibold text-green-600">{discount}% off</span>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-1.5 mt-auto">
                        {rating > 0 ? (
                            <>
                                <span className="inline-flex items-center gap-1 rounded-full bg-green-600 text-white text-[11px] font-semibold px-2 py-0.5">
                                    {rating.toFixed(1)}
                                    <BsStarFill className="text-[9px]" />
                                </span>
                                <span className="text-[11px] text-gray-500">({product.reviewCount || 0})</span>
                            </>
                        ) : (
                            <span className="text-[11px] text-gray-400">No reviews yet</span>
                        )}
                    </div>
                </div>
            </Link>

            <div className="px-3 pb-3">{renderAction()}</div>
        </article>
    );
};

export default ProductCard;